import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Gift, Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const FanPerkButtons = ({ fan, onGift }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const thank = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/creator/fan-perk", { fan_id: fan.id, kind: "thanks" });
      toast.success(`Thank-you sent to ${fan.name}`, { action: { label: "Open chat", onClick: () => navigate(`/chat/${data.match_id}`) } });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1.5">
      <button data-testid={`perk-thank-${fan.id}`} onClick={thank} disabled={busy} title="Send a thank-you"
        className="w-8 h-8 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37]/15 transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Heart size={13} />}
      </button>
      <button data-testid={`perk-gift-${fan.id}`} onClick={() => onGift(fan)} title="Gift a premium post"
        className="w-8 h-8 rounded-full gold-gradient text-black flex items-center justify-center hover:brightness-110 transition-all">
        <Gift size={13} />
      </button>
    </div>
  );
};

export const GiftPostDialog = ({ fan, creatorId, onClose }) => {
  const [posts, setPosts] = useState(null);
  const [sending, setSending] = useState(null);
  useEffect(() => {
    if (!fan) return;
    setPosts(null);
    api.get(`/posts/${creatorId}`).then((r) => setPosts(r.data.filter((p) => p.is_premium && !p.scheduled_for))).catch(() => setPosts([]));
  }, [fan, creatorId]);
  const gift = async (post) => {
    setSending(post.id);
    try {
      await api.post("/creator/fan-perk", { fan_id: fan.id, kind: "unlock", post_id: post.id });
      toast.success(`Unlocked for ${fan.name} — they got a message from you`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't unlock"); } finally { setSending(null); }
  };
  return (
    <Dialog open={!!fan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg" data-testid="gift-post-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Gift a premium post to {fan?.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-[#A0A0A0] -mt-2 mb-2">Pick one of your premium posts. It unlocks for free for this fan only.</div>
        {posts === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
        ) : posts.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#A0A0A0]" data-testid="gift-post-empty"><Lock className="mx-auto mb-2 text-[#D4AF37]" size={22} />Publish a premium post first, then you can gift it.</div>
        ) : (
          <div className="grid grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto">
            {posts.map((p) => (
              <button key={p.id} data-testid={`gift-post-${p.id}`} disabled={!!sending} onClick={() => gift(p)}
                className="relative aspect-square rounded-xl overflow-hidden border border-[#2A2A2A] hover:border-[#D4AF37] transition-colors group disabled:opacity-60">
                {p.type === "video" ? <video src={p.media_url} poster={p.poster_url || undefined} preload="metadata" className="w-full h-full object-cover" />
                  : <img src={p.media_url} alt="" className="w-full h-full object-cover" />}
                <span className="absolute inset-x-0 bottom-0 p-1.5 text-[10px] text-white bg-gradient-to-t from-black/90 to-transparent truncate">{p.caption || "Premium post"}</span>
                {sending === p.id && <span className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></span>}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
